import { VariableNameFinder } from './formulas/variable-name-finder.ts';
import type { ReleaseProfileFormula, ReleaseProfileVariableDefinition } from '../types/release-profiles.ts';
import {
  formulaPredefinedVariables,
  getAllReferencedVarNames,
  notApplicableVar,
  productUsageLbVar,
  productSolidsPercentOfWeightVar,
  productSolidsPercentOfVolumeVar,
  rainfallVar,
} from './formulas/formula-utils.ts';

export function isInventoryProfile(formula: ReleaseProfileFormula): boolean {
  return formula.profileKind === 'inventory';
}

// The same allowlist drives authoring and server validation. Chemical concentrations,
// chemical mixes and waste streams do not describe whole-product inventory amounts.
const inventoryVariableNames = new Set([
  productUsageLbVar,
  productSolidsPercentOfWeightVar,
  productSolidsPercentOfVolumeVar,
  rainfallVar,
  notApplicableVar,
]);
export const inventoryPredefinedVariables = formulaPredefinedVariables
  .filter((variable) => inventoryVariableNames.has(variable.nameForVariable))
  .map((variable) =>
    variable.nameForVariable === notApplicableVar
      ? { ...variable, description: 'This inventory calculation is not applicable.' }
      : variable,
  );

export function isInventoryVariable(definition: ReleaseProfileVariableDefinition): boolean {
  return definition.type === 'constant' || (definition.type === 'question' && !definition.isChemicalSpecific);
}

export function validateProfileKind(
  formula: ReleaseProfileFormula,
  variables: readonly { nameForVariable: string; definition: ReleaseProfileVariableDefinition }[],
  formRLine = '',
  basis = '',
): void {
  const inventory = isInventoryProfile(formula);
  if (inventory && (formRLine || basis))
    throw new Error('Inventory profiles cannot have a Form R line or release basis.');
  const allowed = new Set([
    ...inventoryVariableNames,
    ...variables
      .filter((variable) => isInventoryVariable(variable.definition))
      .map((variable) => variable.nameForVariable),
    ...Object.values(formula.formulas).map((item) => item.nameForVariable),
  ]);
  for (const item of Object.values(formula.formulas)) {
    for (const output of Object.values(item.outputs)) {
      const inventoryOutput = output.type === 'tier2Max' || output.type === 'tier2Avg';
      if (inventory !== inventoryOutput)
        throw new Error('Inventory amounts and release outputs must use separate profiles.');
      // Retain the shared output shape; inferred is an inert placeholder in inventory mode.
      if (inventory && (output.chemicals !== 'inferred' || output.basis)) {
        throw new Error('Inventory outputs cannot select chemicals or specify a release basis.');
      }
    }
    if (inventory) {
      const parsed = VariableNameFinder.findIdentifiersInString(item.formula);
      if (parsed.status === 'success' && parsed.identifiers.some((identifier) => identifier.annotation)) {
        throw new Error('Inventory formulas cannot use chemical annotations.');
      }
      for (const name of getAllReferencedVarNames(item.formula, formula.formulas)) {
        if (!allowed.has(name))
          throw new Error(
            `Variable "${name}" is not supported in inventory profiles. Use constants or non-chemical questions.`,
          );
      }
    }
  }
}
